import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, InputBase, Typography, useTheme } from '@mui/material';
import React, { useCallback, useState } from 'react';

import { ActiveFiltersBar } from './ActiveFiltersBar';
import { AddFilterModal } from './AddFilterModal';
import { AdvancedFilter, FilterBarProps, FilterState } from './FilterBar.props';
import { MultiSelectDropdown } from './MultiSelectDropdown';

const DEFAULT_FILTER_STATE: FilterState = {
  statuses: [],
  jobTypes: [],
  contracts: '',
  jobId: '',
  multiSearch: '',
  advancedFilters: [],
};

export const FilterBar: React.FC<FilterBarProps> = ({
  onSearch,
  statuses,
  statusGroups,
  jobTypes,
  initialFilters,
}) => {
  const theme = useTheme();
  const [filters, setFilters] = useState<FilterState>({
    ...DEFAULT_FILTER_STATE,
    ...initialFilters,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [recentlyUsed, setRecentlyUsed] = useState<string[]>([]);

  const updateFilter = useCallback(<K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSearch = useCallback(() => {
    onSearch(filters);
  }, [filters, onSearch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch]
  );

  const handleAddFilter = useCallback((filter: AdvancedFilter) => {
    setFilters((prev) => ({
      ...prev,
      advancedFilters: [...prev.advancedFilters, filter],
    }));
    setRecentlyUsed((prev) =>
      prev.includes(filter.dataKey) ? prev : [filter.dataKey, ...prev].slice(0, 5)
    );
  }, []);

  const handleRemoveFilter = useCallback((index: number) => {
    setFilters((prev) => ({
      ...prev,
      advancedFilters: prev.advancedFilters.filter((_, i) => i !== index),
    }));
  }, []);

  const handleClearAll = useCallback(() => {
    setFilters((prev) => ({ ...prev, advancedFilters: [] }));
  }, []);

  const inputSx = {
    border: `1px solid ${theme.palette.v4.filterInputBorder}`,
    borderRadius: 1,
    px: 1,
    py: 0.25,
    fontSize: '0.75rem',
    bgcolor: theme.palette.v4.surface,
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          p: 1.25,
          bgcolor: theme.palette.v4.surface,
          border: `1px solid ${theme.palette.v4.border}`,
          borderRadius: 1,
        }}
        onKeyDown={handleKeyDown}
      >
        <MultiSelectDropdown
          label="Status"
          options={statuses}
          selected={filters.statuses}
          onChange={(val) => updateFilter('statuses', val)}
          groups={statusGroups}
        />
        <MultiSelectDropdown
          label="Job Type"
          options={jobTypes}
          selected={filters.jobTypes}
          onChange={(val) => updateFilter('jobTypes', val)}
        />
        <InputBase
          placeholder="Contract"
          value={filters.contracts}
          onChange={(e) => updateFilter('contracts', e.target.value)}
          inputProps={{ 'aria-label': 'Contract filter' }}
          sx={{ ...inputSx, width: 140 }}
        />

        <InputBase
          placeholder="Job ID"
          value={filters.jobId}
          onChange={(e) => updateFilter('jobId', e.target.value)}
          inputProps={{ 'aria-label': 'Job ID filter' }}
          sx={{ ...inputSx, width: 120 }}
        />

        <Button
          startIcon={<AddIcon />}
          onClick={() => setModalOpen(true)}
          aria-label="Add advanced filter"
          sx={{
            textTransform: 'none',
            color: theme.palette.text.secondary,
            border: `1px solid ${theme.palette.v4.filterInputBorder}`,
            borderRadius: 1,
            px: 1,
            py: 0.25,
          }}
        >
          <Typography variant="body2">Add Filter</Typography>
        </Button>

        <InputBase
          placeholder="Search all fields..."
          value={filters.multiSearch}
          onChange={(e) => updateFilter('multiSearch', e.target.value)}
          inputProps={{ 'aria-label': 'Multi-field search' }}
          sx={{ ...inputSx, width: 240, ml: 'auto' }}
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          aria-label="Search services"
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.v4.accent,
            px: 3,
            '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 },
          }}
        >
          Search
        </Button>
      </Box>

      <ActiveFiltersBar
        filters={filters.advancedFilters}
        onRemove={handleRemoveFilter}
        onClearAll={handleClearAll}
      />

      <AddFilterModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onApply={handleAddFilter}
        recentlyUsed={recentlyUsed}
      />
    </Box>
  );
};
